import { observer, useLocalObservable } from 'mobx-react';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../buttons/IconButton';
import { Tooltip } from 'react-tooltip';
import { PUNCTUATION } from '../../../utils/constants/notation';
import { getLogoUrl, LOGO_PLACEHOLDER } from '../../../utils/logo';
import { useStore } from '../../../store/store';
import { observable } from 'mobx';
import pubSub from '../../../utils/pubsub';
import { isPhoneOrTablet } from '../../../utils/utils';
import { createDefaultLegendSelected, orderCompaniesForChartDefault } from './chartSortDefaults';

const escapeHtml = (str) => {
  if (!str) return str;
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const tooltipFormatter = (params, companies, avgEbitMargin, showEbit = true) => {
  if (!params || params.length === 0) return '';

  const dataIndex = params[0].dataIndex;
  const company = companies[dataIndex];

  if (!company) return '';

  const formatSales = (value) => {
    return value ? value : PUNCTUATION.EMPTY_VALUE;
  };

  const formatEbit = (value) => {
    return value ? value : PUNCTUATION.EMPTY_VALUE;
  };

  const formatEbitMarginAvg = (value) => {
    if (!value) {
      return PUNCTUATION.EMPTY_VALUE;
    }
    return value.toFixed(1) + '%';
  };

  const formatCAGR = (value) => {
    return value ? value : PUNCTUATION.EMPTY_VALUE;
  };

  const formatEbitMargin = (value) => {
    return value ? value : PUNCTUATION.EMPTY_VALUE;
  };

  const extendFormatEbitMargin = (company) => {
    const formattedEbitMargin = formatEbitMargin(company.ebitPercentage);
    if (formattedEbitMargin === PUNCTUATION.EMPTY_VALUE) {
      return formattedEbitMargin;
    }

    const delta = company._heatmapEbitMargin - avgEbitMargin;
    if (delta === 0) {
      return formattedEbitMargin;
    }

    const symbol = delta > 0 ? '▲' : '▼';
    const colorSymbol = delta > 0 ? '#5bb07f' : '#d63e5e';
    const colorText = delta > 0 ? '#54a174' : '#c93453';

    return `${formattedEbitMargin}<span style="color: ${colorSymbol};">${symbol}</span><span style="color:${colorText}; font-size: 12px;">${delta.toFixed(1)}%</span>`;
  };

  const tooltipContent = `
    <div style="font-weight: bold; margin-bottom: 0px;">${company.name ? `${escapeHtml(company.name.slice(0, 50))}${company.name.length > 50 ? '...' : ''}` : PUNCTUATION.EMPTY_VALUE}</div>
    <div style="font-weight: normal; margin-bottom: 8px;">${escapeHtml(company.headquarters) || PUNCTUATION.EMPTY_VALUE}</div>
    <div style="display: flex; justify-content: space-between; column-gap: 24px; margin-bottom: 4px;">
      <div style="display: flex; align-items: center;">
        <span style="display: inline-block; width: 10px; height: 10px; background-color: #60a5fa; margin-right: 8px;"></span>
        Sales (€M)
      </div>
      <div>${formatSales(company.revenue)}</div>
    </div>
    ${
      showEbit
        ? `<div style="display: flex; justify-content: space-between; column-gap: 24px; margin-bottom: 4px;">
      <div style="display: flex; align-items: center;">
        <span style="display: inline-block; width: 10px; height: 10px; background-color: #9ca3af; margin-right: 8px;"></span>
        EBIT (€M)
      </div>
      <div>${formatEbit(company.ebit)}</div>
    </div>`
        : ''
    }
    <div style="display: flex; justify-content: space-between; column-gap: 24px; margin-bottom: 4px;">
      <div style="display: flex; align-items: center;">
        <span style="display: inline-block; width: 10px; height: 10px; background-color: #06b6d4; margin-right: 8px; border-radius: 5px;"></span>
        CAGR 3Y
      </div>
      <div>${formatCAGR(company.cagr3Y)}</div>
    </div>
    ${
      showEbit
        ? `<div style="display: flex; justify-content: space-between; column-gap: 24px;">
      <div style="display: flex; align-items: center;">
        <span style="display: inline-block; width: 10px; height: 10px; background-color: #8b5cf6; margin-right: 8px; border-radius: 5px;"></span>
        EBIT Margin
      </div>
      <div>${extendFormatEbitMargin(company)}</div>
    </div>
    <div style="width: 100%; height: 1px; background-color: #000000; transform: scaleY(0.1); margin-bottom: 4px; margin-top: 4px;"></div>
    <div style="display: flex; justify-content: space-between; column-gap: 24px; margin-bottom: 4px;">
      <div style="display: flex; align-items: center;">
        <span style="display: inline-block; width: 10px; height: 10px; background-color: #10b981; margin-right: 8px; border-radius: 5px;"></span>
        EBIT Margin (median)
      </div>
      <div>${formatEbitMarginAvg(avgEbitMargin)}</div>
    </div>`
        : ''
    }
  `;

  return tooltipContent;
};

const calculateMedianEbitMargin = (companies) => {
  const validMargins = companies.filter((c) => c.ebitPercentage).map((c) => c._heatmapEbitMargin);

  if (validMargins.length === 0) {
    return 0;
  }

  validMargins.sort((a, b) => a - b);
  const mid = Math.floor(validMargins.length / 2);

  if (validMargins.length % 2 === 0) {
    return (validMargins[mid - 1] + validMargins[mid]) / 2;
  } else {
    return validMargins[mid];
  }
};

const RenderTables = observer(
  ({
    visibleCompanies,
    leftPadding,
    rightPadding,
    companyColumnWidth,
    getHoverBackgroundColor,
    handleTableColumnHover,
    handleTableColumnLeave,
    firstCompanyId,
    parentCompany,
    onCompanyClick,
    project,
    getIsExcludingResult,
    excludeResult,
    showRationale,
    hoveredCompanyId,
    showEbit = true
  }) => {
    const { utilsStore } = useStore();
    const state = useLocalObservable(() => ({
      toggledLinkContextMenu: {},
      toggleLinkContextMenu: (companyId) => {
        if (companyId) {
          state.toggledLinkContextMenu[companyId] = !state.toggledLinkContextMenu[companyId];
        } else {
          state.toggledLinkContextMenu = {};
        }
      }
    }));

    useEffect(() => {
      const onAnyClick = () => {
        state.toggleLinkContextMenu();
      };

      const onPubSubContextClick = (payload) => {
        if (payload?.event === 'icon-button-context-menu-trigger') {
          state.toggleLinkContextMenu();
        }
      };

      pubSub.subscribe(onPubSubContextClick);
      document.addEventListener('click', onAnyClick);

      return () => {
        pubSub.unsubscribe(onPubSubContextClick);
        document.removeEventListener('click', onAnyClick);
      };
    }, [state]);

    const showExcludeRow = !!getIsExcludingResult && !!excludeResult;

    return (
      <>
        {/* Percentage Values Table */}
        <div className="percentage-values-table">
          <div className="pvt-inner">
            <div className="pvt-flex">
              {/* Left padding column to match chart */}
              <div
                className="left"
                style={{
                  width: `${leftPadding}px`,
                  minWidth: `${leftPadding}px`,
                  maxWidth: `${leftPadding}px`
                }}
              >
                <div>CAGR 3Y</div>
                {showEbit && <div>EBIT Margin</div>}
              </div>

              {/* Percentage columns - now using company ID for hover */}
              {visibleCompanies.map((company, index) => (
                <div
                  key={company.id || index}
                  className="column"
                  style={{
                    width: `${companyColumnWidth}px`,
                    minWidth: `${companyColumnWidth}px`,
                    maxWidth: `${companyColumnWidth}px`,
                    backgroundColor: getHoverBackgroundColor(company)
                  }}
                  onMouseEnter={() => handleTableColumnHover(company.id)}
                  onMouseLeave={handleTableColumnLeave}
                >
                  <div>
                    <div className="revenue-percentage">
                      {company.cagr3Y || PUNCTUATION.EMPTY_VALUE}
                    </div>
                  </div>
                  {showEbit && (
                    <div>
                      <div className="ebit-percentage">
                        {company.ebitPercentage || PUNCTUATION.EMPTY_VALUE}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Right padding column to match chart */}
              <div
                className="right"
                style={{
                  width: `${rightPadding}px`,
                  minWidth: `${rightPadding}px`,
                  maxWidth: `${rightPadding}px`
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* Company Details Table */}
        <div className="company-details-table" style={{ width: '100%', cursor: 'default' }}>
          <div className="cdt-inner">
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {/* Company Row */}
              <div className="cdt-flex">
                <div
                  className="left"
                  style={{
                    width: `${leftPadding}px`,
                    minWidth: `${leftPadding}px`,
                    maxWidth: `${leftPadding}px`
                  }}
                ></div>
                {visibleCompanies.map((company, index) => {
                  const logoSize = companyColumnWidth > 90 ? 90 : companyColumnWidth;
                  const logoUrl = getLogoUrl(
                    (company.id === firstCompanyId ? parentCompany : company).website_SAFE_LOGO
                  );
                  return (
                    <div
                      key={`company-${company.id || index}`}
                      className="logo"
                      style={{
                        width: `${companyColumnWidth}px`,
                        minWidth: `${companyColumnWidth}px`,
                        maxWidth: `${companyColumnWidth}px`,
                        color: company.id === firstCompanyId ? '#348cf9' : '#000000',
                        backgroundColor: getHoverBackgroundColor(company)
                      }}
                      onMouseEnter={() => handleTableColumnHover(company.id)}
                      onMouseLeave={handleTableColumnLeave}
                    >
                      <img
                        id={`company-details-bar-chart-company-logo-${company.id}`}
                        style={{
                          width: `${logoSize - 4}px`,
                          height: `${logoSize - 4}px`,
                          cursor: 'pointer'
                        }}
                        src={logoUrl || LOGO_PLACEHOLDER}
                        onClick={() =>
                          onCompanyClick(company.id === firstCompanyId ? parentCompany : company)
                        }
                        onContextMenu={(event) => {
                          event.preventDefault();
                          pubSub.publish({ event: 'icon-button-context-menu-trigger' });
                          state.toggleLinkContextMenu(company.id);
                        }}
                      />
                      {state.toggledLinkContextMenu[company.id] && (
                        <Tooltip
                          anchorSelect={`#company-details-bar-chart-company-logo-${company.id}`}
                          multiline={true}
                          variant="dark"
                          opacity={1}
                          place="bottom-center"
                          clickable={true}
                          isOpen={true}
                          className="icon-button-tooltip-context-link-menu"
                        >
                          <div
                            className="context-link-menu-item"
                            onClick={() => {
                              const targetCompany =
                                company.id === firstCompanyId ? parentCompany : company;
                              utilsStore.openCompanyDetails(targetCompany.id, {
                                ref: targetCompany.name
                              });
                            }}
                          >
                            <div className="left-side">
                              <div className="context-link-icon-wrap">
                                <img src={ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT} />
                              </div>
                              <div className="context-link-text" style={{ textAlign: 'left' }}>
                                Open in new tab
                                <br />
                                <div
                                  style={{
                                    maxWidth: '112px',
                                    fontSize: '8px',
                                    lineHeight: '10px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    textAlign: 'left'
                                  }}
                                >
                                  {company.name.trim()}
                                </div>
                              </div>
                            </div>
                            <div className="right-side"></div>
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  );
                })}
                <div
                  className="right"
                  style={{
                    width: `${rightPadding}px`,
                    minWidth: `${rightPadding}px`,
                    maxWidth: `${rightPadding}px`
                  }}
                ></div>
              </div>

              {/* Description Row */}
              <div className="cdt-flex">
                <div
                  className="left"
                  style={{
                    width: `${leftPadding}px`,
                    minWidth: `${leftPadding}px`,
                    maxWidth: `${leftPadding}px`
                  }}
                >
                  Description
                </div>
                {visibleCompanies.map((company, index) => (
                  <Fragment key={`desc-${company.id || index}`}>
                    <div
                      id={`company-details-bar-chart-desc-tooltip-${company.id || index}`}
                      className="description"
                      style={{
                        width: `${companyColumnWidth}px`,
                        minWidth: `${companyColumnWidth}px`,
                        maxWidth: `${companyColumnWidth}px`,
                        color: company.id === firstCompanyId ? '#348cf9' : '#000000',
                        backgroundColor: getHoverBackgroundColor(company)
                      }}
                      onMouseEnter={() => handleTableColumnHover(company.id)}
                      onMouseLeave={handleTableColumnLeave}
                    >
                      {company.description || PUNCTUATION.EMPTY_VALUE}
                    </div>
                    <Tooltip
                      anchorSelect={`#company-details-bar-chart-desc-tooltip-${company.id || index}`}
                      multiline={true}
                      variant="light"
                      place={'bottom-center'}
                      opacity={1}
                      content={company.description || PUNCTUATION.EMPTY_VALUE}
                      className={'company-details-bar-chart-desc-tooltip'}
                      style={{ textAlign: 'left' }}
                    />
                  </Fragment>
                ))}
                <div
                  className="right"
                  style={{
                    width: `${rightPadding}px`,
                    minWidth: `${rightPadding}px`,
                    maxWidth: `${rightPadding}px`
                  }}
                ></div>
              </div>

              {/* Rationale Row */}
              {showRationale && (
                <div className="cdt-flex">
                  <div
                    className="left"
                    style={{
                      width: `${leftPadding}px`,
                      minWidth: `${leftPadding}px`,
                      maxWidth: `${leftPadding}px`
                    }}
                  >
                    Rationale
                  </div>
                  {visibleCompanies.map((company, index) => (
                    <div
                      key={`rationale-${company.id || index}`}
                      className="rationale"
                      style={{
                        width: `${companyColumnWidth}px`,
                        minWidth: `${companyColumnWidth}px`,
                        maxWidth: `${companyColumnWidth}px`,
                        color: company.id === firstCompanyId ? '#2563eb' : '#6b7280',
                        fontWeight: company.id === firstCompanyId ? '600' : 'normal',
                        backgroundColor: getHoverBackgroundColor(company)
                      }}
                      onMouseEnter={() => handleTableColumnHover(company.id)}
                      onMouseLeave={handleTableColumnLeave}
                    >
                      {company.showScoring && (
                        <>
                          <div
                            className="result-scoring-badge noselect"
                            id={`result-scoring-badge-table-${company.id}`}
                            style={{ backgroundColor: company.scoreSecondaryColor }}
                          >
                            <div className="score-icon-wrap">
                              <img
                                src={ICON_BUTTON_ICONS.EXCLAMATAION_CIRCLE}
                                style={{ filter: company.scoreIconFilter }}
                              />
                            </div>
                            <div
                              className="score-label"
                              style={{ color: company.scorePrimaryColor }}
                            >
                              {company.scoreLabel}
                            </div>
                          </div>
                          <Tooltip
                            anchorSelect={`#result-scoring-badge-table-${company.id}`}
                            multiline={true}
                            variant="light"
                            place={'bottom-center'}
                            opacity={1}
                            content={company.scoreTooltipText}
                            className={company.scoreTooltipClassName}
                            style={{ textAlign: 'left' }}
                            clickable
                          />
                        </>
                      )}
                    </div>
                  ))}
                  <div
                    className="right"
                    style={{
                      width: `${rightPadding}px`,
                      minWidth: `${rightPadding}px`,
                      maxWidth: `${rightPadding}px`
                    }}
                  ></div>
                </div>
              )}

              {/* Exclusion Row */}
              {showExcludeRow && (
                <div className="cdt-flex">
                  <div
                    className="left"
                    style={{
                      width: `${leftPadding}px`,
                      minWidth: `${leftPadding}px`,
                      maxWidth: `${leftPadding}px`
                    }}
                  ></div>
                  {visibleCompanies.map((company, index) => {
                    const showExclusionBtn =
                      firstCompanyId !== company.id &&
                      (isPhoneOrTablet || hoveredCompanyId === company.id);
                    const isInExcludeAction = showExclusionBtn
                      ? getIsExcludingResult(company.id)
                      : false;
                    return (
                      <div
                        key={`exclusion-${company.id || index}`}
                        className="exclusion"
                        style={{
                          width: `${companyColumnWidth}px`,
                          minWidth: `${companyColumnWidth}px`,
                          maxWidth: `${companyColumnWidth}px`,
                          color: company.id === firstCompanyId ? '#2563eb' : '#6b7280',
                          fontWeight: company.id === firstCompanyId ? '600' : 'normal',
                          backgroundColor: getHoverBackgroundColor(company)
                        }}
                        onMouseEnter={() => handleTableColumnHover(company.id)}
                        onMouseLeave={handleTableColumnLeave}
                      >
                        {showExclusionBtn && (
                          <>
                            {company.isExcluded && (
                              <IconButton
                                type={ICON_BUTTON_TYPES.BLUE}
                                icon={ICON_BUTTON_ICONS.ARROW_COUNTERCLOCKWISE}
                                tooltipText="Restore result"
                                onClick={() => excludeResult(company, false)}
                                disabled={isInExcludeAction}
                              />
                            )}
                            {!company.isExcluded && (
                              <IconButton
                                type={ICON_BUTTON_TYPES.RED}
                                icon={ICON_BUTTON_ICONS.X_LG}
                                tooltipText="Exclude result"
                                onClick={() => excludeResult(company, true)}
                                disabled={isInExcludeAction}
                              />
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                  <div
                    className="right"
                    style={{
                      width: `${rightPadding}px`,
                      minWidth: `${rightPadding}px`,
                      maxWidth: `${rightPadding}px`
                    }}
                  ></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
);

const sessionState = observable({});

const ProfilerBarChart = observer(
  ({
    companies: companiesProp = [],
    parentCompany = null,
    onCompanyClick = () => {},
    chartId,
    sort,
    project,
    getIsExcludingResult,
    excludeResult,
    // TD-2890 — drop the EBIT bars, margin lines and their legend/tooltip/table rows when
    // the set carries no margins. The surfaces that hide the Chart view outright for
    // EBIT-less accounts never pass this; the team feedback view keeps the chart for its
    // Sales and CAGR series and passes false.
    showEbit = true
  }) => {
    const { utilsStore } = useStore();

    // The Chart view ranks by EBIT margin descending while no sort is chosen; an
    // explicit selection is already applied upstream and comes through untouched.
    // Bar 0 stays put when a parent company leads the set (comparables).
    const companies = useMemo(
      () => orderCompaniesForChartDefault(companiesProp, sort, !!parentCompany, showEbit),
      [companiesProp, sort, parentCompany, showEbit]
    );

    if (!sessionState[chartId]) {
      sessionState[chartId] = {
        legendSelected: createDefaultLegendSelected(showEbit),
        firstVisibleCompanyId: null,
        sort
      };
    }

    useEffect(() => {
      if (sessionState[chartId] && sessionState[chartId].sort !== sort) {
        sessionState[chartId].firstVisibleCompanyId = null;
        sessionState[chartId].sort = sort;
      }
    }, [chartId, sort]);

    const showRationale = useMemo(() => {
      return companies.some((company) => company.scoreLabel);
    }, [companies]);

    const state = useLocalObservable(() => ({
      containerWidth: 0,
      setContainerWidth: (width) => {
        if (state.containerWidth !== width) {
          state.containerWidth = width;
        }
      },
      containerHeight: 0,
      setContainerHeight: (height) => {
        if (state.containerHeight !== height) {
          state.containerHeight = height;
        }
      },
      visibleCompanies: [],
      setVisibleCompanies: (companies) => {
        state.visibleCompanies = companies;
      },
      hoveredCompanyId: null,
      setHoveredCompanyId: (companyId) => {
        state.hoveredCompanyId = companyId;
      },
      get legendSelected() {
        return sessionState[chartId]?.legendSelected || createDefaultLegendSelected(showEbit);
      },
      setLegendSelected: (selected) => {
        if (!sessionState[chartId]) {
          sessionState[chartId] = {};
        }
        sessionState[chartId].legendSelected = selected;
      },
      get firstVisibleCompanyId() {
        return sessionState[chartId]?.firstVisibleCompanyId;
      },
      setFirstVisibleCompanyId: (companyId) => {
        if (!sessionState[chartId]) {
          sessionState[chartId] = {};
        }
        sessionState[chartId].firstVisibleCompanyId = companyId;
      }
    }));

    const chartRef = useRef(null);
    const containerRef = useRef(null);
    const outerContainerRef = useRef(null);
    const updateVisibleCompaniesRef = useRef(null);

    // ResizeObserver for tracking container dimensions
    useEffect(() => {
      if (!containerRef.current || !outerContainerRef.current) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;

          if (entry.target === containerRef.current) {
            state.setContainerWidth(width);
          } else if (entry.target === outerContainerRef.current) {
            state.setContainerHeight(height);
          }
        }
      });

      resizeObserver.observe(containerRef.current);
      resizeObserver.observe(outerContainerRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }, [state]);

    useEffect(() => {
      // Handle when companies change (exclusions)
      if (sessionState[chartId]?.firstVisibleCompanyId) {
        const firstVisibleExists = companies.find(
          (c) => c.id === sessionState[chartId].firstVisibleCompanyId
        );

        if (!firstVisibleExists && companies.length > 0 && state.visibleCompanies.length > 0) {
          // First visible was excluded, find the next best company to maintain position
          const nextBestCompany = state.visibleCompanies.find((vc) =>
            companies.find((c) => c.id === vc.id)
          );

          if (nextBestCompany) {
            sessionState[chartId].firstVisibleCompanyId = nextBestCompany.id;
          } else {
            sessionState[chartId].firstVisibleCompanyId = companies[0].id;
          }
        }
      }
    }, [companies, chartId, state.visibleCompanies]);

    useEffect(() => {
      if (!chartRef.current || companies.length === 0 || state.containerWidth === 0) return;

      const chart = echarts.init(chartRef.current);

      chart.off('dataZoom');
      chart.off('mouseout');
      chart.off('legendselectchanged');

      const firstCompanyId = parentCompany ? (companies.length > 0 ? companies[0].id : null) : null;

      const avgEbitMargin = calculateMedianEbitMargin(companies);

      const [leftPadding, rightPadding, minBarWidth, optimalBarWidth, barGap] = [
        80, 80, 32, 32, 10
      ];
      const axisSpace = leftPadding + rightPadding;
      const availableWidth = state.containerWidth - axisSpace;

      const calculateMaxCompanies = (targetBarWidth) => {
        const internalBarGap = targetBarWidth * 0.2;
        const spacePerCompany = targetBarWidth * 2 + internalBarGap + barGap;
        return Math.floor(availableWidth / spacePerCompany);
      };

      let maxCompaniesVisible = calculateMaxCompanies(optimalBarWidth);
      let targetBarWidth = optimalBarWidth;

      if (maxCompaniesVisible < 1) {
        maxCompaniesVisible = calculateMaxCompanies(minBarWidth);
        targetBarWidth = minBarWidth;
      }

      maxCompaniesVisible = Math.max(1, Math.min(maxCompaniesVisible, companies.length));

      const calculateBarWidth = (numCompanies) => {
        if (numCompanies === 0) return targetBarWidth;
        const widthPerCompany = availableWidth / numCompanies;
        const effectiveWidth = widthPerCompany - barGap;
        const calculatedBarWidth = effectiveWidth / 2.5;
        return Math.max(minBarWidth, Math.min(optimalBarWidth, Math.floor(calculatedBarWidth)));
      };

      const dynamicBarWidth = calculateBarWidth(maxCompaniesVisible);

      let initialVisibleCompanies = companies.slice(0, maxCompaniesVisible);
      const savedFirstVisibleCompanyId = state.firstVisibleCompanyId;

      if (savedFirstVisibleCompanyId) {
        const savedCompanyIndex = companies.findIndex(
          (company) => company.id === savedFirstVisibleCompanyId
        );
        if (savedCompanyIndex !== -1) {
          const startIndex = Math.max(
            0,
            Math.min(savedCompanyIndex, companies.length - maxCompaniesVisible)
          );
          const endIndex = Math.min(startIndex + maxCompaniesVisible, companies.length);
          initialVisibleCompanies = companies.slice(startIndex, endIndex);
          if (initialVisibleCompanies[0] !== savedFirstVisibleCompanyId) {
            state.setFirstVisibleCompanyId(initialVisibleCompanies[0].id);
          }
        }
      }

      state.setVisibleCompanies(initialVisibleCompanies);

      const companyNames = companies.map(
        (company, index) => company.name || PUNCTUATION.EMPTY_VALUE
      );
      const salesData = companies.map((company, index) => ({
        value: company._heatmapRevenue || 0,
        itemStyle: { color: company.id === firstCompanyId ? '#3b82f6' : '#60a5fa' }
      }));
      const ebitData = companies.map((company, index) => ({
        value: company._heatmapEbit || 0,
        itemStyle: { color: company.id === firstCompanyId ? '#4b5563' : '#9ca3af' }
      }));

      const ebitMarginData = companies.map((company) =>
        company.ebitPercentage != null ? company._heatmapEbitMargin || 0 : null
      );
      const yoyGrowthData = companies.map((company) =>
        company.cagr3Y != null ? company._heatmapCagr3Y || 0 : null
      );
      const avgEbitMarginData = companies.map(() => avgEbitMargin);

      const calculateAxisRanges = (legendSelected) => {
        const selectedLeftSeries = [];
        const selectedRightSeries = [];

        if (legendSelected['Sales']) {
          selectedLeftSeries.push(...companies.map((c) => c._heatmapRevenue || 0));
        }
        if (legendSelected['EBIT']) {
          selectedLeftSeries.push(...companies.map((c) => c._heatmapEbit || 0));
        }
        if (legendSelected['CAGR 3Y']) {
          selectedRightSeries.push(...companies.map((c) => c._heatmapCagr3Y || 0));
        }
        if (legendSelected['EBIT Margin']) {
          selectedRightSeries.push(...companies.map((c) => c._heatmapEbitMargin || 0));
        }
        if (legendSelected['EBIT Margin (median)']) {
          selectedRightSeries.push(avgEbitMargin);
        }

        const minLeftValue = selectedLeftSeries.length > 0 ? Math.min(...selectedLeftSeries) : 0;
        const maxLeftValue = selectedLeftSeries.length > 0 ? Math.max(...selectedLeftSeries) : 100;
        const minRightValue = selectedRightSeries.length > 0 ? Math.min(...selectedRightSeries) : 0;
        const maxRightValue =
          selectedRightSeries.length > 0 ? Math.max(...selectedRightSeries) : 100;

        return { minLeftValue, maxLeftValue, minRightValue, maxRightValue };
      };

      const { minLeftValue, maxLeftValue, minRightValue, maxRightValue } = calculateAxisRanges(
        state.legendSelected
      );

      let initialDataZoomStart = 0;
      let initialDataZoomEnd =
        companies.length > maxCompaniesVisible
          ? (maxCompaniesVisible / companies.length) * 100
          : 100;

      if (savedFirstVisibleCompanyId) {
        const savedCompanyIndex = companies.findIndex(
          (company) => company.id === savedFirstVisibleCompanyId
        );

        if (savedCompanyIndex !== -1) {
          const denom = Math.max(1, companies.length - 1);
          const startPercent = Math.max(0, (savedCompanyIndex / denom) * 100);
          const endPercent = Math.min(
            100,
            ((savedCompanyIndex + maxCompaniesVisible - 1) / denom) * 100
          );

          initialDataZoomStart = startPercent;
          initialDataZoomEnd = endPercent;
        }
      }

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
            label: {
              show: true,
              backgroundColor: '#348cf9',
              color: '#fff' // Optional: text color
            }
          },
          formatter: function (params) {
            return tooltipFormatter(params, companies, avgEbitMargin, showEbit);
          }
        },
        legend: {
          data: [
            {
              name: 'Sales',
              itemStyle: { color: '#60a5fa' }
            },
            ...(showEbit
              ? [
                  {
                    name: 'EBIT',
                    itemStyle: { color: '#9ca3af' }
                  }
                ]
              : []),
            'CAGR 3Y',
            ...(showEbit ? ['EBIT Margin', 'EBIT Margin (median)'] : [])
          ],
          top: 0,
          left: 'center',
          itemGap: 20,
          selected: (() => {
            const selected = state.legendSelected;
            return selected;
          })()
        },
        grid: {
          left: 80,
          right: 80,
          top: 60,
          bottom: 70,
          containLabel: false
        },
        dataZoom: [
          {
            type: 'inside',
            xAxisIndex: 0,
            orient: 'horizontal',
            start: initialDataZoomStart,
            end: initialDataZoomEnd,
            zoomLock: true,
            moveOnMouseMove: true,
            moveOnMouseWheel: true,
            throttle: 100,
            rangeMode: ['percent', 'percent'],
            minValueSpan: maxCompaniesVisible - 1,
            maxValueSpan: Math.min(maxCompaniesVisible, companies.length) - 1
          },
          {
            type: 'slider',
            xAxisIndex: 0,
            show: true,
            bottom: 0,
            height: 40,
            left: 77,
            right: 82,
            start: initialDataZoomStart,
            end: initialDataZoomEnd,
            minSpan: (maxCompaniesVisible / companies.length) * 100,
            maxSpan: 100,
            handleSize: 0,
            handleStyle: {
              color: '#3b82f6',
              borderColor: '#1e40af'
            },
            brushSelect: false,
            fillerColor: 'rgba(59, 130, 246, 0.2)',
            backgroundColor: '#f9fafb',
            showDetail: false,
            dataBackground: {
              lineStyle: {
                width: 0
              },
              areaStyle: {
                color: 'rgba(59, 130, 246, 0.2)'
              }
            },
            emphasis: {
              handleStyle: {
                color: '#3b82f6',
                borderColor: '#1e40af'
              }
            }
          }
        ],
        xAxis: [
          {
            type: 'category',
            data: companyNames,
            axisPointer: {
              type: 'shadow',
              shadowStyle: {
                color: 'rgba(96, 165, 250, 0.07)'
              }
            },
            axisLabel: {
              show: false
            },
            axisTick: {
              show: false
            },
            axisLine: {
              show: true,
              lineStyle: {
                color: '#ffffff'
              }
            }
          }
        ],
        yAxis: [
          {
            type: 'value',
            name: '',
            position: 'left',
            min: minLeftValue,
            max: maxLeftValue,
            axisLabel: {
              show: true,
              formatter: function (value) {
                return (value / 1000000).toFixed(1) + 'M';
              }
            },
            axisPointer: {
              label: {
                formatter: function (params) {
                  return (params.value / 1000000).toFixed(1) + 'M';
                }
              }
            },
            splitLine: {
              show: false
            }
          },
          {
            type: 'value',
            name: '',
            position: 'right',
            min: minRightValue,
            max: maxRightValue,
            axisLabel: {
              show: true,
              formatter: '{value}%'
            },
            axisPointer: {
              label: {
                formatter: function (params) {
                  return params.value.toFixed(1) + '%';
                }
              }
            },
            splitLine: {
              show: false
            }
          }
        ],
        series: [
          {
            name: 'Sales',
            type: 'bar',
            data: salesData,
            barGap: '20%',
            barCategoryGap: '30%',
            barWidth: dynamicBarWidth,
            cursor: 'default'
          },
          ...(showEbit
            ? [
                {
                  name: 'EBIT',
                  type: 'bar',
                  data: ebitData,
                  barWidth: dynamicBarWidth,
                  cursor: 'default'
                },
                {
                  name: 'EBIT Margin (median)',
                  type: 'line',
                  yAxisIndex: 1,
                  data: avgEbitMarginData,
                  lineStyle: {
                    color: '#10b981',
                    width: 2
                  },
                  itemStyle: {
                    color: '#10b981'
                  },
                  symbol: 'none',
                  cursor: 'default'
                }
              ]
            : []),
          {
            name: 'CAGR 3Y',
            type: 'line',
            yAxisIndex: 1,
            data: yoyGrowthData,
            lineStyle: {
              color: '#06b6d4',
              width: 2,
              type: 'dashed'
            },
            itemStyle: {
              color: '#06b6d4'
            },
            symbol: 'circle',
            symbolSize: 6,
            cursor: 'default',
            connectNulls: false
          },
          ...(showEbit
            ? [
                {
                  name: 'EBIT Margin',
                  type: 'line',
                  yAxisIndex: 1,
                  data: ebitMarginData,
                  lineStyle: {
                    color: '#8b5cf6',
                    width: 2
                  },
                  itemStyle: {
                    color: '#8b5cf6'
                  },
                  symbol: 'circle',
                  symbolSize: 6,
                  cursor: 'default',
                  connectNulls: false
                }
              ]
            : [])
        ]
      };

      chart.setOption(option);

      chart.on('legendselectchanged', (params) => {
        const currentOption = chart.getOption();
        const currentDataZoom = currentOption.dataZoom[0];
        const currentStart = currentDataZoom.start;
        const currentEnd = currentDataZoom.end;

        state.setLegendSelected(params.selected);

        const {
          minLeftValue: newMinLeft,
          maxLeftValue: newMaxLeft,
          minRightValue: newMinRight,
          maxRightValue: newMaxRight
        } = calculateAxisRanges(params.selected);

        chart.setOption({
          yAxis: [
            {
              min: newMinLeft,
              max: newMaxLeft
            },
            {
              min: newMinRight,
              max: newMaxRight
            }
          ],
          dataZoom: [
            {
              start: currentStart,
              end: currentEnd
            },
            {
              start: currentStart,
              end: currentEnd
            }
          ]
        });
      });

      chart.on('mouseout', (params) => {
        if (params.componentType === 'series' || params.componentType === 'xAxis') {
          state.setHoveredCompanyId(null);

          chart.dispatchAction({
            type: 'hideTip'
          });
        }
      });

      chart.getZr().on('mousemove', (e) => {
        const chartDom = chartRef.current;
        const currentOption = chart.getOption();
        const grid = currentOption.grid[0];

        const chartWidth = chartDom.offsetWidth;
        const chartHeight = chartDom.offsetHeight;

        const gridLeft =
          typeof grid.left === 'string' && grid.left.includes('%')
            ? (parseFloat(grid.left) / 100) * chartWidth
            : typeof grid.left === 'number'
              ? grid.left
              : leftPadding;
        const gridRight =
          typeof grid.right === 'string' && grid.right.includes('%')
            ? chartWidth - (parseFloat(grid.right) / 100) * chartWidth
            : chartWidth - (typeof grid.right === 'number' ? grid.right : rightPadding);
        const gridTop =
          typeof grid.top === 'string' && grid.top.includes('%')
            ? (parseFloat(grid.top) / 100) * chartHeight
            : typeof grid.top === 'number'
              ? grid.top
              : 60;
        const gridBottom =
          typeof grid.bottom === 'string' && grid.bottom.includes('%')
            ? chartHeight - (parseFloat(grid.bottom) / 100) * chartHeight
            : chartHeight - (typeof grid.bottom === 'number' ? grid.bottom : 90);

        const mouseX = e.offsetX;
        const mouseY = e.offsetY;

        const isInChartArea =
          mouseX >= gridLeft && mouseX <= gridRight && mouseY >= gridTop && mouseY <= gridBottom;

        if (!isInChartArea) {
          state.setHoveredCompanyId(null);
          return;
        }

        const visibleCompanies = state.visibleCompanies;
        if (visibleCompanies.length === 0) return;

        const availableGridWidth = gridRight - gridLeft;
        const companyWidth = availableGridWidth / visibleCompanies.length;

        const relativeX = mouseX - gridLeft;

        const hoveredIndex = Math.floor(relativeX / companyWidth);

        if (hoveredIndex >= 0 && hoveredIndex < visibleCompanies.length) {
          const hoveredCompany = visibleCompanies[hoveredIndex];
          if (hoveredCompany && hoveredCompany.id !== state.hoveredCompanyId) {
            state.setHoveredCompanyId(hoveredCompany.id);

            const fullCompanyIndex = companies.findIndex(
              (company) => company.id === hoveredCompany.id
            );
            if (fullCompanyIndex !== -1) {
              chart.dispatchAction({
                type: 'highlight',
                seriesIndex: [0, 1],
                dataIndex: fullCompanyIndex
              });
            }
          }
        }
      });

      chart.getZr().on('mouseout', () => {
        state.setHoveredCompanyId(null);
      });

      const updateVisibleCompanies = () => {
        try {
          const currentOption = chart.getOption();
          const dataZoomOption = currentOption.dataZoom && currentOption.dataZoom[0];

          if (!dataZoomOption) return;

          const currentMaxCompanies = Math.max(1, Math.min(maxCompaniesVisible, companies.length));

          let startIndex = 0;
          let endIndex = companies.length;

          if (typeof dataZoomOption.startValue === 'number') {
            startIndex = dataZoomOption.startValue;
          }
          if (typeof dataZoomOption.endValue === 'number') {
            endIndex = dataZoomOption.endValue + 1;
          }

          if (startIndex === 0 && endIndex === companies.length) {
            const startPercent = dataZoomOption.start || 0;
            const endPercent = dataZoomOption.end || 100;
            startIndex = Math.floor((startPercent / 100) * companies.length);
            endIndex = Math.ceil((endPercent / 100) * companies.length);
          }

          startIndex = Math.max(0, Math.min(startIndex, companies.length - 1));
          endIndex = Math.max(startIndex + 1, Math.min(endIndex, companies.length));

          const newVisibleCompanies = companies.slice(startIndex, endIndex);

          const limitedVisibleCompanies =
            newVisibleCompanies.length > currentMaxCompanies
              ? newVisibleCompanies.slice(0, currentMaxCompanies)
              : newVisibleCompanies;

          const newBarWidth = calculateBarWidth(limitedVisibleCompanies.length);

          const currentBarWidth = chart.getOption().series[0].barWidth;
          if (Math.abs(newBarWidth - currentBarWidth) > 2) {
            chart.setOption({
              series: [
                {
                  name: 'Sales',
                  barWidth: newBarWidth
                },
                {
                  name: 'EBIT',
                  barWidth: newBarWidth
                }
              ]
            });
          }

          const currentIds = state.visibleCompanies.map((c) => c.id || c.name).join('|');
          const newIds = limitedVisibleCompanies.map((c) => c.id || c.name).join('|');

          if (currentIds !== newIds && limitedVisibleCompanies.length > 0) {
            state.setVisibleCompanies(limitedVisibleCompanies);

            if (limitedVisibleCompanies.length > 0) {
              state.setFirstVisibleCompanyId(limitedVisibleCompanies[0].id);
            }
          }
        } catch (error) {
          // console.warn('Error getting visible companies:', error);
        }
      };

      updateVisibleCompaniesRef.current = updateVisibleCompanies;

      chart.on('dataZoom', () => {
        updateVisibleCompanies();
      });

      setTimeout(updateVisibleCompanies, 200);

      return () => {
        chart.dispose();
      };
    }, [
      state,
      companies,
      state.containerWidth,
      state.containerHeight,
      utilsStore.windowWidth,
      chartId,
      // TD-2890 — the option object is built with `showEbit` baked in, so the chart has to
      // be rebuilt when it flips (results arriving turn it on). `parentCompany` stays out,
      // as it was before: it only reaches this effect through `companies`, which is already
      // a dependency.
      showEbit
    ]);

    const handleTableColumnHover = (companyId) => {
      state.setHoveredCompanyId(companyId);

      const dataIndex = companies.findIndex((company) => company.id === companyId);

      if (chartRef.current && dataIndex !== -1) {
        const chart = echarts.getInstanceByDom(chartRef.current);
        if (chart) {
          chart.dispatchAction({
            type: 'highlight',
            seriesIndex: [0, 1],
            dataIndex: dataIndex
          });

          chart.dispatchAction({
            type: 'showTip',
            seriesIndex: 0,
            dataIndex: dataIndex
          });
        }
      }
    };

    const handleTableColumnLeave = () => {
      state.setHoveredCompanyId(null);

      if (chartRef.current) {
        const chart = echarts.getInstanceByDom(chartRef.current);
        if (chart) {
          chart.dispatchAction({
            type: 'downplay'
          });

          chart.dispatchAction({
            type: 'hideTip'
          });

          chart.dispatchAction({
            type: 'updateAxisPointer',
            currTrigger: 'leave'
          });
        }
      }
    };

    const firstCompanyId = parentCompany ? (companies.length > 0 ? companies[0].id : null) : null;

    const [leftPadding, rightPadding] = [80, 80];
    const availableChartWidth = state.containerWidth - leftPadding - rightPadding;
    const companyColumnWidth =
      state.visibleCompanies.length > 0 ? availableChartWidth / state.visibleCompanies.length : 180;

    const getHoverBackgroundColor = (company) => {
      if (state.hoveredCompanyId === company.id) {
        return 'rgba(96, 165, 250, 0.07)';
      }
      return 'transparent';
    };

    return (
      <div className="bar-chart-container-wrap" ref={containerRef}>
        <div className="bar-chart-container-outer" ref={outerContainerRef}>
          <div ref={chartRef} className="bar-chart-container-inner" />
        </div>
        <RenderTables
          showEbit={showEbit}
          visibleCompanies={state.visibleCompanies}
          leftPadding={leftPadding}
          rightPadding={rightPadding}
          companyColumnWidth={companyColumnWidth}
          getHoverBackgroundColor={getHoverBackgroundColor}
          handleTableColumnHover={handleTableColumnHover}
          handleTableColumnLeave={handleTableColumnLeave}
          firstCompanyId={firstCompanyId}
          parentCompany={parentCompany}
          onCompanyClick={onCompanyClick}
          project={project}
          getIsExcludingResult={getIsExcludingResult}
          excludeResult={excludeResult}
          showRationale={showRationale}
          hoveredCompanyId={state.hoveredCompanyId}
        />
      </div>
    );
  }
);

export default ProfilerBarChart;
